import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Modal,
  Breadcrumb,
  BreadcrumbItem,
  Nav,
  ButtonGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { parseUserInfo } from "../../utils/parseUtil";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  useGetEmployeeListMutation,
  useGetOptionsMutation,
  useGetReportTypesMutation,
} from "../../slices/commonApiSlice";
import { parseOptionsData } from "../../utils/parseOptionsData";
import {
  useGetForm14ForEmployeeMutation,
  useGetLeaveRejoinReportsMutation,
} from "../../slices/adminApiSlice";
import { Typeahead } from "react-bootstrap-typeahead";
import { parseReportType } from "../../utils/parseReportType";

const EmployeePersonalFileDataScreen = () => {
  const [title, setTitle] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [selectReportIds, setSelectReportIds] = useState([]);
  const [selectReportNames, setSelectReportNames] = useState([]);
  const [reportType, setReportType] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [selectedEmpCode, setSelectedEmpCode] = useState("");
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [empName, setEmpName] = useState("");
  const [selectedSingleReport, setSelectedSingleReport] = useState(null); // For single selection
  const [selectedMultiReports, setSelectedMultiReports] = useState([]); // For multi-selection
  const [fileExtension, setFileExtension] = useState("");

  const { userInfo } = useSelector((state) => state.auth);

  const [getReportTypes, { isLoading: getReportTypesLoading }] =
    useGetReportTypesMutation();
  const [getLeaveRejoinReports, { isLoading }] =
    useGetLeaveRejoinReportsMutation();
  const [getEmpName, { isLoading: getEmpNameLoading }] =
    useGetEmployeeListMutation();

  useEffect(() => {
    const fetchReportTypes = async () => {
      try {
        const reqCompanyCode = userInfo[13]?.InfoField || "1";

        const formData = new FormData();
        formData.append("title", "GetReportsMasterList");
        formData.append("ReqCompanyCode", reqCompanyCode);
        formData.append("ReqRptType", 50);

        const res = await getReportTypes(formData);
        const parsedOptions = parseUserInfo(res);
        const parsedData = parseReportType(parsedOptions);

        if (parsedData) {
          setReportType(parsedData);
        } else {
          toast.error("Failed to load report types");
        }
      } catch (err) {
        console.error("Error fetching report types:", err);
        toast.error("Error fetching report types");
      }
    };
    fetchReportTypes();
  }, [getReportTypes, userInfo]);

  const handleSingleSelect = (selected) => {
    if (selected.length > 0) {
      setSelectedSingleReport(selected[0]);
    } else {
      setSelectedSingleReport(null);
    }
  };

  // Multi-selection report toggle (RAR)
  const handleToggleMultiSelect = (report) => {
    if (selectedMultiReports.includes(report.rptID)) {
      setSelectedMultiReports((prev) =>
        prev.filter((id) => id !== report.rptID)
      );
      setSelectReportNames((prev) =>
        prev.filter((name) => name !== report.rptHeading)
      );
    } else {
      setSelectedMultiReports((prev) => [...prev, report.rptID]);
      setSelectReportNames((prev) => [...prev, report.rptHeading]);
    }
  };

  // Toggle Select All / Deselect All
  const handleSelectAllToggle = () => {
    if (selectedMultiReports.length === reportType.length) {
      setSelectedMultiReports([]);
      setSelectReportNames([]);
    } else {
      const allIds = reportType.map((report) => report.rptID);
      const allNames = reportType.map((report) => report.rptHeading);
      setSelectedMultiReports(allIds);
      setSelectReportNames(allNames);
    }
  };

  const handleSubmit = async (e, title) => {
    e.preventDefault();

    const reqCompanyCode = userInfo[13]?.InfoField;
    const formData = new FormData();
    formData.append("title", title);
    formData.append("ReqCompanyCode", reqCompanyCode);
    formData.append("ReqEmpCode", empCode);

    if (title === "GetLeaveRejoinReports") {
      if (selectedSingleReport?.rptHeading) {
        formData.append(
          "ReqSeleRptName",
          selectedSingleReport?.rptHeading || ""
        );
        formData.append("ReqSelectedRptId", selectedSingleReport?.rptID || "");
      } else {
        return toast.info("select a report");
      }
    } else {
      formData.append("ReqSeleRptName", selectReportNames.join(", "));
      formData.append("ReqSelectedRptId", selectedMultiReports.join(", "));
    }

    // Log formData contents
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    try {
      const res = await getLeaveRejoinReports(formData);
      const parsedData = parseUserInfo(res);
      console.log(parsedData, "DATA");
      if (parsedData && parsedData.length > 0) {
        const data = parsedData[0];
        if (data.ErrorCode === "0") {
          let filePath = data.JSONData1.replace(
            "/api/WebServiceEmployeeForms.aspx/",
            "/"
          );
          const backendUrl = process.env.REACT_APP_URL;
          const fullPath = `${backendUrl}${filePath}`;
          setPdfUrl(fullPath);

          // Determine file extension correctly
          const extension = fullPath.split(".").pop().toLowerCase();
          if (extension === "pdf" || extension === "rar") {
            setFileExtension(extension);
          } else {
            setFileExtension("");
          }

          setShowModal(true);
        } else {
          toast.error(data.ErrorMessage || "An error occurred");
        }
      }
    } catch (err) {
      toast.error("Error submitting data");
    }
  };

  const handleEmployeeSearch = async (input) => {
    if (input.length < 3) {
      setEmployeeSuggestions([]);
      return;
    }

    try {
      const reqCompanyCode = userInfo[13]?.InfoField || "1";
      const formData = new FormData();
      formData.append("title", "GetEmployeeList");
      formData.append("ReqCompanyCode", reqCompanyCode);
      formData.append("ReqSearchStr", input);

      const res = await getEmpName(formData);
      const parsedData = parseUserInfo(res);

      if (parsedData && parsedData.length > 0) {
        const employeeList = JSON.parse(parsedData[0].JSONData1);
        setEmployeeSuggestions(employeeList);
      } else {
        setEmployeeSuggestions([]);
        toast.error("No employees found");
      }
    } catch (err) {
      console.error("Error fetching employee list:", err);
      toast.error("Error fetching employee list");
    }
  };

  // const [selectedOptions, setSelectedOptions] = useState([])

  // const handleToggle = (report) => {
  //   if (selectedOptions.includes(report.rptID)) {
  //     setSelectedOptions(prevSelected => prevSelected.filter(id => id !== report.rptID))
  //     setSelectReportIds(prevIds => prevIds.filter(id => id !== report.rptID))
  //     setSelectReportNames(prevNames => prevNames.filter(name => name !== report.rptHeading))
  //   } else {
  //     setSelectedOptions(prevSelected => [...prevSelected, report.rptID])
  //     setSelectReportIds(prevIds => [...prevIds, report.rptID])
  //     setSelectReportNames(prevNames => [...prevNames, report.rptHeading])
  //   }
  // }

  // const handleSelectAllToggle = () => {
  //   if (selectedOptions.length === reportType.length) {
  //     setSelectedOptions([])
  //     setSelectReportIds([])
  //     setSelectReportNames([])
  //   } else {
  //     const allIds = reportType.map(report => report.rptID)
  //     const allNames = reportType.map(report => report.rptHeading)
  //     setSelectedOptions(allIds)
  //     setSelectReportIds(allIds)
  //     setSelectReportNames(allNames)
  //   }
  // };

  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem>
          <Nav.Link
            as={Link}
            to="/dashboard"
            className="text-success text-decoration-none"
          >
            Dashboard
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Employee Personal File Data</BreadcrumbItem>
      </Breadcrumb>

      <Container fluid className="px-4" style={{ maxWidth: "1400px" }}>
        <h4 className="text-center mb-3">Employee Personal File Data</h4>

        <Form
          className="border border-2 rounded-lg shadow-sm bg-light p-4 mb-4"
          onSubmit={handleSubmit}
        >
          {/* Employee Details Section */}
          <div className="border border-secondary border-2 rounded-lg p-3 mb-3 shadow-sm">
            <Row className="">
              <Col sm={6} md={6} lg={3}>
                <Form.Group controlId="empName">
                  <Form.Label className="fw-semibold">Employee Name</Form.Label>
                  <Typeahead
                    id="empName-typeahead"
                    onInputChange={(input) => {
                      setEmpName(input);
                      handleEmployeeSearch(input);
                    }}
                    onChange={(selected) => {
                      if (selected.length > 0) {
                        setEmpName(selected[0].EmployeeName);
                        setSelectedEmpCode(selected[0].Empcode);
                        setEmpCode(selected[0].Empcode);
                        setFatherName(selected[0].FatherName);
                      }
                    }}
                    options={employeeSuggestions}
                    labelKey="EmployeeName"
                    placeholder="Choose an employee..."
                    selected={empName ? [{ EmployeeName: empName }] : []}
                    className="form-control-hover"
                    minLength={3}
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={6} lg={3}>
                <Form.Group controlId="fatherName" className="mb-3">
                  <Form.Label className="fw-semibold">Father Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Father Name"
                    value={fatherName}
                    onChange={(e) => setFatherName(e.target.value)}
                    className="form-control-hover"
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={6} lg={3}>
                <Form.Group controlId="empCode" className="mb-3">
                  <Form.Label className="fw-semibold">Employee Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Employee Code"
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    className="form-control-hover"
                    required
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={6} lg={3}>
                <Form.Group controlId="reportType">
                  <Form.Label className="fw-semibold">
                    Report Type (Single Selection)
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedSingleReport?.rptHeading || ""}
                    onChange={(e) => {
                      const selectedReport = reportType.find(
                        (report) => report.rptHeading === e.target.value
                      );
                      handleSingleSelect(
                        selectedReport ? [selectedReport] : []
                      );
                    }}
                    className="form-control-hover"
                    required
                  >
                    <option value="" disabled>
                      Choose a report...
                    </option>
                    {reportType.map((report, index) => (
                      <option key={index} value={report.rptHeading}>
                        {report.rptHeading}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

           
            

            <Row className="text-center">
              <Col>
                <Button
                  type="submit"
                  variant="success"
                  className="px-4"
                  onClick={(e) => handleSubmit(e, "GetLeaveRejoinReports")}
                >
                  PRINT PDF
                </Button>
              </Col>
            </Row>
          </div>

          {/* Multi-Select Report Section */}
          <div className="border border-secondary border-2 rounded-lg p-3 shadow-sm">
            <Row className="align-items-center mb-2">
              <Col xs="auto">
                <span className="fw-semibold">Report Type:</span>
              </Col>
              <Col className="d-flex justify-content-center">
                <Col
                  xs={12}
                  md="auto"
                  className="d-flex justify-content-center"
                >
                  <Button
                    type="submit"
                    variant="warning"
                    className="px-4 fw-semibold w-100 w-md-auto"
                    style={{
                      minWidth: "180px", // Ensures a consistent width on larger screens
                      maxWidth: "250px", // Prevents it from becoming too wide
                      fontSize: "1rem",
                    }}
                    onClick={(e) =>
                      handleSubmit(e, "GetLeaveRejoinSelectedReports")
                    }
                  >
                    PRINT SELECTED (RAR)
                  </Button>
                </Col>
              </Col>
              <Col xs="auto">
                <ButtonGroup>
                  <Button
                    variant="primary"
                    size="sm"
                    className="fw-semibold"
                    onClick={handleSelectAllToggle}
                  >
                    {selectedMultiReports.length === reportType.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <div className="bg-light border rounded p-3">
                  <div className="d-flex flex-wrap" style={{ gap: "7px" }}>
                    {reportType.map((report) => (
                      <Button
                        key={report.rptID}
                        size="sm"
                        className={`position-relative shadow-sm ${
                          selectedMultiReports.includes(report.rptID)
                            ? "hover-lift"
                            : ""
                        }`}
                        style={{
                          flex: "1 1 150px",
                          maxWidth: "180px",
                          minHeight: "45px",
                          padding: "8px 12px",
                          transition: "all 0.2s ease",
                          borderRadius: "8px",
                        }}
                        variant={
                          selectedMultiReports.includes(report.rptID)
                            ? "success"
                            : "outline-primary"
                        }
                        onClick={() => handleToggleMultiSelect(report)}
                      >
                        {selectedMultiReports.includes(report.rptID) && (
                          <span
                            className="position-absolute top-0 end-0 translate-middle badge bg-danger"
                            style={{
                              padding: "6px",
                              borderRadius: "50%",
                              fontSize: "0.7rem",
                              border: "2px solid white",
                            }}
                          >
                            ✓
                          </span>
                        )}
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <span
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: "500",
                              whiteSpace: "normal",
                              textAlign: "center",
                              lineHeight: "1.2",
                            }}
                          >
                            {report.rptHeading}
                          </span>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Form>

        {/* Modal for Report */}
        <Modal
          size="lg"
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {fileExtension === "pdf" ? (
              <iframe
                src={pdfUrl}
                title="Report PDF"
                width="100%"
                height="500px"
              />
            ) : (
              <a href={pdfUrl} download>
                <Button variant="primary">Download Report (.rar)</Button>
              </a>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default EmployeePersonalFileDataScreen;
